import {
    useState, useCallback, useRef, useEffect,
} from 'react';
import { Modal } from 'react-rainbow-components';
import { useConnectModal } from '@rainbow-modules/hooks';
import { isEmpty } from '@rainbow-modules/validation';
import Button from 'components/Button';
import ModalTitle from 'components/ModalTitle';
import useAttachFiles from 'hooks/useAttachFiles';
import FeedbackInput, { FeedbackInputRef } from './feedbackInput';
import { REPORT_FEEDBACK_MODAL, IMAGE_FORMATS_ACCEPTED_STRING } from '../../constants';
import { ModalContent, ModalDescription } from './styled';

const ReportFeedbackModal = () => {
    const connectedModalProps = useConnectModal(REPORT_FEEDBACK_MODAL, { title: 'Report Issue' });
    const {
        isOpen,
        title = 'Report Issue',
        agentId,
        context,
        onRequestClose = () => {},
        onSubmit = () => {},
        ...otherProps
    } = connectedModalProps;

    const [feedback, setFeedback] = useState('');
    const feedbackInputRef = useRef<FeedbackInputRef>(null);
    const {
        files: attachedFiles,
        attach: attachFiles,
        remove: removeAttachedFile,
        clear: clearAttachedFiles,
    } = useAttachFiles({
        acceptedFormats: IMAGE_FORMATS_ACCEPTED_STRING,
    });

    const handleSubmit = useCallback(
        async () => (onSubmit as CallableFunction)({
            text: feedback,
            attachedFiles,
        }),
        [feedback, attachedFiles, onSubmit],
    );

    const handleClose = useCallback(() => {
        (onRequestClose as CallableFunction)();
        clearAttachedFiles();
    }, [onRequestClose, clearAttachedFiles]);

    useEffect(
        () => {
            if (!isOpen) {
                setFeedback('');
                clearAttachedFiles();
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isOpen],
    );

    const footer = (
        <div className="rainbow-flex rainbow-justify_end rainbow-gap_small">
            <Button
                variant="border"
                onClick={handleClose}
                borderRadius="semi-square"
            >
                Cancel
            </Button>
            <Button
                variant="brand"
                onClick={handleSubmit}
                disabled={isEmpty(feedback.trim())}
                borderRadius="semi-square"
            >
                Send
            </Button>
        </div>
    );

    return (
        <Modal
            title={<ModalTitle>{title}</ModalTitle>}
            isOpen={isOpen as boolean}
            footer={footer}
            onRequestClose={handleClose}
            size="large"
            borderRadius="semi-square"
            {...otherProps}
        >
            <ModalContent>
                <ModalDescription>
                    Reporting issues helps us maintain accuracy and improve results over time. With
                    your support, we can address problems sooner and deliver a more reliable
                    experience for you and others.
                </ModalDescription>
                <FeedbackInput
                    ref={feedbackInputRef}
                    value={feedback}
                    onChange={setFeedback}
                    attachedFiles={attachedFiles}
                    onAttachFiles={attachFiles}
                    onRemoveFile={removeAttachedFile}
                    placeholder="Describe the issue or provide feedback..."
                    mode="messages"
                />
            </ModalContent>
        </Modal>
    );
};

export default ReportFeedbackModal;
